import React from 'react';
import { colors, spacing, borderRadius, shadows } from '../../styles/designTokens';

/**
 * ErrorMessage Component
 * 
 * Standardized error display component with retry functionality.
 * Replaces inconsistent error handling throughout the application.
 * 
 * Usage:
 * <ErrorMessage 
 *   message="Failed to load dashboard data"
 *   onRetry={loadData}
 * />
 * 
 * <ErrorMessage 
 *   title="Connection Error"
 *   message="Please check your internet connection and try again."
 *   severity="error"
 *   dismissible
 *   onDismiss={handleDismiss}
 * />
 * 
 * Props:
 * - message: Error message (required)
 * - title: Optional title/heading
 * - severity: 'error' | 'warning' | 'info' (default: 'error')
 * - onRetry: Optional retry callback function
 * - dismissible: Boolean - shows dismiss button
 * - onDismiss: Callback when dismissed
 * - icon: Custom icon (overrides default)
 * - className: Additional CSS classes
 */

const ErrorMessage = ({
  message,
  title,
  severity = 'error',
  onRetry,
  dismissible = false,
  onDismiss,
  icon,
  className = ''
}) => {
  // Severity configuration
  const severityConfig = {
    error: {
      bg: '#FEE2E2', // red-100
      border: '#FECACA', // red-200
      text: colors.error.dark,
      icon: '❌',
      titleColor: colors.error.main
    },
    warning: {
      bg: '#FEF3C7', // yellow-100
      border: '#FDE68A', // yellow-200
      text: colors.warning.dark,
      icon: '⚠️',
      titleColor: colors.warning.main
    },
    info: {
      bg: '#CFFAFE', // cyan-100
      border: '#A5F3FC', // cyan-200
      text: colors.info.dark,
      icon: 'ℹ️',
      titleColor: colors.info.main
    }
  };

  const config = severityConfig[severity] || severityConfig.error;
  const displayIcon = icon || config.icon;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`rounded-lg p-4 ${className}`}
      style={{
        backgroundColor: config.bg,
        border: `1px solid ${config.border}`,
        borderRadius: borderRadius.lg,
        boxShadow: shadows.sm
      }}
    >
      <div className="flex">
        {/* Icon */}
        <div className="flex-shrink-0">
          <span className="text-xl" aria-hidden="true">
            {displayIcon}
          </span>
        </div>

        {/* Content */}
        <div className="ml-3 flex-1">
          {/* Title */}
          {title && (
            <h3 
              className="text-sm font-semibold mb-1"
              style={{ color: config.titleColor }}
            >
              {title}
            </h3>
          )}

          {/* Message */}
          <p 
            className="text-sm"
            style={{ color: config.text }}
          >
            {message}
          </p>

          {/* Action buttons */}
          {onRetry && (
            <div className="mt-3">
              <button
                onClick={onRetry}
                className="text-sm font-medium px-3 py-1 rounded hover:opacity-80 transition-opacity"
                style={{
                  backgroundColor: config.border,
                  color: config.text
                }}
              >
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Dismiss button */}
        {dismissible && onDismiss && (
          <div className="ml-auto pl-3">
            <button
              onClick={onDismiss}
              className="inline-flex rounded-md p-1.5 hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                color: config.text,
                focusRingColor: config.border
              }}
              aria-label="Dismiss"
            >
              <svg 
                className="h-5 w-5" 
                viewBox="0 0 20 20" 
                fill="currentColor"
                aria-hidden="true"
              >
                <path 
                  d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" 
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * ErrorBoundaryFallback Component
 * 
 * Fallback UI for React Error Boundaries
 * 
 * Usage:
 * <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export const ErrorBoundaryFallback = ({ error, resetErrorBoundary }) => {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <ErrorMessage
          title="Something went wrong"
          message={
            process.env.NODE_ENV === 'development'
              ? error.message
              : 'We\'re having trouble loading this page. Please try refreshing.'
          }
          severity="error"
          onRetry={resetErrorBoundary}
        />
      </div>
    </div>
  );
};

/**
 * InlineError Component
 * 
 * Small inline error for form fields
 * 
 * Usage:
 * <InlineError message="Email is required" />
 */
export const InlineError = ({ message, className = '' }) => {
  if (!message) return null;

  return (
    <p
      role="alert"
      className={`text-sm mt-1 ${className}`}
      style={{ color: colors.error.main }}
    >
      <span aria-hidden="true">⚠ </span>
      {message}
    </p>
  );
};

/**
 * ErrorPage Component
 * 
 * Full-page error display
 * 
 * Usage:
 * <ErrorPage 
 *   title="404 - Page Not Found"
 *   message="The page you're looking for doesn't exist."
 *   actionLabel="Go to Dashboard"
 *   onAction={() => navigate('/dashboard')}
 * />
 */
export const ErrorPage = ({ 
  title, 
  message, 
  actionLabel, 
  onAction 
}) => {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className="text-center max-w-md">
        {/* Large error icon */}
        <div className="mb-6">
          <span className="text-6xl" aria-hidden="true">❌</span>
        </div>

        {/* Title */}
        <h1 
          className="text-2xl font-bold mb-3"
          style={{ color: colors.error[700] }}
        >
          {title || 'An Error Occurred'}
        </h1>

        {/* Message */}
        <p 
          className="text-base mb-6"
          style={{ color: colors.neutral[600] }}
        >
          {message || 'Something went wrong. Please try again.'}
        </p>

        {/* Action button */}
        {actionLabel && onAction && (
          <button
            onClick={onAction}
            className="px-4 py-2 rounded-lg font-medium text-white transition-colors"
            style={{
              backgroundColor: colors.primary.main
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary.dark;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary.main;
            }}
          >
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorMessage;

